(() => {
  var __defProp = Object.defineProperty;
  var __typeError = (msg) => {
    throw TypeError(msg);
  };
  var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
  var __publicField = (obj, key, value) => __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  var __accessCheck = (obj, member, msg) => member.has(obj) || __typeError("Cannot " + msg);
  var __privateGet = (obj, member, getter) => (__accessCheck(obj, member, "read from private field"), getter ? getter.call(obj) : member.get(obj));
  var __privateAdd = (obj, member, value) => member.has(obj) ? __typeError("Cannot add the same private member more than once") : member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
  var __privateSet = (obj, member, value, setter) => (__accessCheck(obj, member, "write to private field"), setter ? setter.call(obj, value) : member.set(obj, value), value);
  var __privateMethod = (obj, member, method) => (__accessCheck(obj, member, "access private method"), method);

  // src/scripts/_base/utilities.js
  var requestIdleCallback = typeof window.requestIdleCallback == "function" ? window.requestIdleCallback : setTimeout;
  var viewTransition = {
    current: void 0
  };
  function debounce(fn, wait) {
    let timeout;
    function debounced(...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn.apply(this, args), wait);
    }
    debounced.cancel = () => {
      clearTimeout(timeout);
    };
    return (
      /** @type {T & { cancel(): void }} */
      debounced
    );
  }
  var reducedMotion = matchMedia("(prefers-reduced-motion: reduce)");
  function prefersReducedMotion() {
    return reducedMotion.matches;
  }
  function onAnimationEnd(elements, callback, options = { subtree: true }) {
    const animations = Array.isArray(elements) ? elements.flatMap((element) => element.getAnimations(options)) : elements.getAnimations(options);
    const animationPromises = animations.reduce(
      (acc, animation) => {
        if (animation.timeline instanceof DocumentTimeline) {
          acc.push(animation.finished);
        }
        return acc;
      },
      /** @type {Promise<Animation>[]} */
      []
    );
    return Promise.allSettled(animationPromises).then(callback);
  }
  function isClickedOutside(event, element) {
    if (event.target instanceof HTMLDialogElement || !(event.target instanceof Element)) {
      return !isPointWithinElement(event.clientX, event.clientY, element);
    }
    return !element.contains(event.target);
  }
  function isPointWithinElement(x, y, element) {
    const { left, right, top, bottom } = element.getBoundingClientRect();
    return x >= left && x <= right && y >= top && y <= bottom;
  }
  var mediaQueryLarge = matchMedia("(min-width: 750px)");
  var _queue, _scheduled;
  var Scheduler = class {
    constructor() {
      /** @type {Set<() => void>} */
      __privateAdd(this, _queue, /* @__PURE__ */ new Set());
      /** @type {boolean} */
      __privateAdd(this, _scheduled, false);
      /** @param {() => void} task */
      __publicField(this, "schedule", async (task) => {
        __privateGet(this, _queue).add(task);
        if (!__privateGet(this, _scheduled)) {
          __privateSet(this, _scheduled, true);
          if (viewTransition.current) await viewTransition.current;
          requestAnimationFrame(this.flush);
        }
      });
      __publicField(this, "flush", () => {
        for (const task of __privateGet(this, _queue)) {
          setTimeout(task, 0);
        }
        __privateGet(this, _queue).clear();
        __privateSet(this, _scheduled, false);
      });
    }
  };
  _queue = new WeakMap();
  _scheduled = new WeakMap();
  var scheduler = new Scheduler();
  if (typeof Theme !== "undefined") {
    Theme.utilities = {
      ...Theme.utilities,
      scheduler
    };
  }

  // src/scripts/_base/component.js
  var DeclarativeShadowElement = class extends HTMLElement {
    connectedCallback() {
      if (!this.shadowRoot) {
        const template = this.querySelector(':scope > template[shadowrootmode="open"]');
        if (!(template instanceof HTMLTemplateElement)) return;
        const shadow = this.attachShadow({ mode: "open" });
        shadow.append(template.content.cloneNode(true));
      }
    }
  };
  var _Component_instances, updateRefs_fn, _mutationObserver, _isDescendant;
  var Component = class extends DeclarativeShadowElement {
    constructor() {
      super(...arguments);
      __privateAdd(this, _Component_instances);
      /**
       * An object holding references to child elements with `ref` attributes.
       *
       * @type {RefsType<T>}
       */
      __publicField(
        this,
        "refs",
        /** @type {RefsType<T>} */
        {}
      );
      /**
       * An array of required refs. If a ref is not found, an error will be thrown.
       *
       * @type {string[] | undefined}
       */
      __publicField(this, "requiredRefs");
      /**
       * MutationObserver instance to observe changes in the component's DOM subtree and update refs accordingly.
       *
       * @type {MutationObserver}
       */
      __privateAdd(this, _mutationObserver, new MutationObserver((mutations) => {
        if (mutations.some(
          (m) => m.type === "attributes" && __privateGet(this, _isDescendant).call(this, m.target) || m.type === "childList" && [...m.addedNodes, ...m.removedNodes].some(__privateGet(this, _isDescendant))
        )) {
          __privateMethod(this, _Component_instances, updateRefs_fn).call(this);
        }
      }));
      /**
       * Checks if a given node is a descendant of this component.
       *
       * @param {Node} node - The node to check.
       * @returns {boolean} True if the node is a descendant of this component.
       */
      __privateAdd(this, _isDescendant, (node) => getClosestComponent(getAncestor(node)) === this);
    }
    /**
     * Gets the root node of the component, which is either its shadow root or the component itself.
     *
     * @returns {(ShadowRoot | Component<T>)[]} The root nodes.
     */
    get roots() {
      return this.shadowRoot ? [this, this.shadowRoot] : [this];
    }
    /**
     * Called when the element is connected to the document's DOM.
     *
     * Initializes event listeners and refs.
     */
    connectedCallback() {
      super.connectedCallback();
      registerEventListeners();
      __privateMethod(this, _Component_instances, updateRefs_fn).call(this);
      this.loggerColor = "#5983BD";
      requestIdleCallback(() => {
        for (const root of this.roots) {
          __privateGet(this, _mutationObserver).observe(root, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ["ref"],
            attributeOldValue: true
          });
        }
      });
    }
    /**
     * Called when the element is re-rendered by the Section Rendering API.
     */
    updatedCallback() {
      __privateGet(this, _mutationObserver).takeRecords();
      __privateMethod(this, _Component_instances, updateRefs_fn).call(this);
    }
    /**
     * Called when the element is disconnected from the document's DOM.
     *
     * Disconnects the mutation observer.
     */
    disconnectedCallback() {
      __privateGet(this, _mutationObserver).disconnect();
    }
    /**
     * Prints the given arguments if the component is currently in debug mode;
     * @param  {...any} args 
     */
    log(...args) {
      if (this.debug) {
        console.log(`%c[${this.tagName}]`, `color: ${this.loggerColor}`, ...args);
      }
    }
  };
  _Component_instances = new WeakSet();
  /**
   * Updates the `refs` object by querying all descendant elements with `ref` attributes and storing references to them.
   *
   * This method is called to keep the `refs` object in sync with the DOM.
   */
  updateRefs_fn = function() {
    var _a, _b;
    const refs = (
      /** @type any */
      {}
    );
    const elements = this.roots.reduce(
      (acc, root) => {
        for (const element of root.querySelectorAll("[ref]")) {
          if (!__privateGet(this, _isDescendant).call(this, element)) continue;
          acc.add(element);
        }
        return acc;
      },
      /** @type {Set<Element>} */
      /* @__PURE__ */ new Set()
    );
    for (const ref of elements) {
      const refName = (_a = ref.getAttribute("ref")) != null ? _a : "";
      const isArray = refName.endsWith("[]");
      const path = isArray ? refName.slice(0, -2) : refName;
      if (isArray) {
        const array = Array.isArray(refs[path]) ? refs[path] : [];
        array.push(ref);
        refs[path] = array;
      } else {
        refs[path] = ref;
      }
    }
    if ((_b = this.requiredRefs) == null ? void 0 : _b.length) {
      for (const ref of this.requiredRefs) {
        if (!(ref in refs)) {
          throw new MissingRefError(ref, this);
        }
      }
    }
    this.refs = /** @type {RefsType<T>} */
    refs;
  };
  _mutationObserver = new WeakMap();
  _isDescendant = new WeakMap();
  function getAncestor(node) {
    if (node.parentNode) return node.parentNode;
    const root = node.getRootNode();
    if (root instanceof ShadowRoot) return root.host;
    return null;
  }
  function getClosestComponent(node) {
    if (!node) return null;
    if (node instanceof Component) return node;
    if (node instanceof HTMLElement && node.tagName.toLowerCase().endsWith("-component")) return node;
    const ancestor = getAncestor(node);
    if (ancestor) return getClosestComponent(ancestor);
    return null;
  }
  var initialized = false;
  function registerEventListeners() {
    if (initialized) return;
    initialized = true;
    const events = ["click", "change", "select", "focus", "blur", "submit", "input", "keydown", "keyup", "toggle"];
    const shouldBubble = ["focus", "blur"];
    const expensiveEvents = ["pointerenter", "pointerleave"];
    for (const eventName of [...events, ...expensiveEvents]) {
      const attribute = `on:${eventName}`;
      document.addEventListener(
        eventName,
        (event) => {
          var _a;
          const element = getElement(event);
          if (!element) return;
          const proxiedEvent = event.target !== element ? new Proxy(event, {
            get(target, property) {
              if (property === "target") return element;
              const value2 = Reflect.get(target, property);
              if (typeof value2 === "function") {
                return value2.bind(target);
              }
              return value2;
            }
          }) : event;
          const value = (_a = element.getAttribute(attribute)) != null ? _a : "";
          let [selector, method] = value.split("/");
          const matches = value.match(/([\/\?][^\/\?]+)([\/\?][^\/\?]+)$/);
          const data = matches ? matches[2] : null;
          const instance = selector ? selector.startsWith("#") ? document.querySelector(selector) : element.closest(selector) : getClosestComponent(element);
          if (!(instance instanceof Component) || !method) return;
          method = method.replace(/\?.*/, "");
          const callback = (
            /** @type {any} */
            instance[method]
          );
          if (typeof callback === "function") {
            try {
              const args = [proxiedEvent];
              if (data) args.unshift(parseData(data));
              callback.call(instance, ...args);
            } catch (error) {
              console.error(error);
            }
          }
        },
        { capture: true }
      );
    }
    function getElement(event) {
      var _a, _b;
      const target = (_b = (_a = event.composedPath) == null ? void 0 : _a.call(event)[0]) != null ? _b : event.target;
      if (!(target instanceof Element)) return;
      if (target.hasAttribute(`on:${event.type}`)) {
        return target;
      }
      if (expensiveEvents.includes(event.type)) {
        return null;
      }
      return event.bubbles || shouldBubble.includes(event.type) ? target.closest(`[on\\:${event.type}]`) : null;
    }
  }
  function parseData(str) {
    const delimiter = str[0];
    const data = str.slice(1);
    return delimiter === "?" ? Object.fromEntries(
      Array.from(new URLSearchParams(data).entries()).map(([key, value]) => [key, parseValue(value)])
    ) : parseValue(data);
  }
  function parseValue(str) {
    if (str === "true") return true;
    if (str === "false") return false;
    const maybeNumber = Number(str);
    if (!isNaN(maybeNumber) && str.trim() !== "") return maybeNumber;
    return str;
  }
  var MissingRefError = class extends Error {
    /**
     * @param {string} ref
     * @param {Component} component
     */
    constructor(ref, component) {
      super(`Required ref "${ref}" not found in component ${component.tagName.toLowerCase()}`);
    }
  };

  // src/scripts/_components/dialog.js
  var scrollLockCount = 0;
  function lockScroll() {
    if (scrollLockCount++ > 0) return;
    const html = document.documentElement;
    const scrollbarWidth = Math.max(0, window.innerWidth - html.clientWidth);
    html.style.setProperty("--scrollbar-compensation", `${scrollbarWidth}px`);
    html.style.paddingRight = scrollbarWidth ? `${scrollbarWidth}px` : "";
    html.style.overflow = "hidden";
  }
  function unlockScroll() {
    if (scrollLockCount === 0 || --scrollLockCount > 0) return;
    const html = document.documentElement;
    html.style.overflow = "";
    html.style.paddingRight = "";
    html.style.removeProperty("--scrollbar-compensation");
  }
  var _handleResize, _previousScrollY, _DialogComponent_instances, handleClick_fn, handleKeyDown_fn;
  var DialogComponent = class extends Component {
    constructor(focusElement) {
      super();
      __privateAdd(this, _DialogComponent_instances);
      __publicField(this, "requiredRefs", ["dialog"]);
      __privateAdd(this, _handleResize, debounce(() => {
        const { minWidth, maxWidth } = this;
        if (!minWidth && !maxWidth) return;
        const windowWidth = window.innerWidth;
        if (windowWidth < minWidth || windowWidth > maxWidth) {
          this.closeDialog();
        }
      }, 50));
      __privateAdd(this, _previousScrollY, 0);
      /**
       * Closes the dialog.
       */
      __publicField(this, "closeDialog", async () => {
        const { dialog } = this.refs;
        if (!dialog.open) return;
        this.removeEventListener("click", __privateMethod(this, _DialogComponent_instances, handleClick_fn));
        this.removeEventListener("keydown", __privateMethod(this, _DialogComponent_instances, handleKeyDown_fn));
        dialog.style.animation = "none";
        void dialog.offsetWidth;
        dialog.classList.add("dialog-closing");
        dialog.style.animation = "";
        await onAnimationEnd(dialog, void 0, {
          subtree: false
        });
        unlockScroll();
        dialog.close();
        dialog.classList.remove("dialog-closing");
        dialog.classList.remove("open");
        this.removeTrapFocus();
        this.dispatchEvent(new DialogCloseEvent());
      });
      /**
       * Toggles the dialog.
       */
      __publicField(this, "toggleDialog", () => {
        if (this.refs.dialog.open) {
          this.closeDialog();
        } else {
          this.showDialog();
        }
      });
      this.focusElement = focusElement;
      this.trapFocusHandlers = {};
    }
    connectedCallback() {
      super.connectedCallback();
      if (this.minWidth || this.maxWidth) {
        window.addEventListener("resize", __privateGet(this, _handleResize));
      }
    }
    disconnectedCallback() {
      super.disconnectedCallback();
      if (this.minWidth || this.maxWidth) {
        window.removeEventListener("resize", __privateGet(this, _handleResize));
      }
    }
    /**
     * Shows the dialog.
     */
    showDialog() {
      const { dialog } = this.refs;
      if (dialog.open) return;
      __privateSet(this, _previousScrollY, window.scrollY);
      requestAnimationFrame(() => {
        lockScroll();
        dialog.showModal();
        dialog.classList.add("open");
        this.dispatchEvent(new DialogOpenEvent());
        this.trapFocus(dialog, this.focusElement);
        this.addEventListener("click", __privateMethod(this, _DialogComponent_instances, handleClick_fn));
        this.addEventListener("keydown", __privateMethod(this, _DialogComponent_instances, handleKeyDown_fn));
      });
    }
    /**
     * Handles focus trap for the current dialog
     */
    trapFocus(container, elementToFocus = container) {
      this.removeTrapFocus();
      this.trapFocusHandlers.keydown = (event) => {
        if (!event.code || event.code.toUpperCase() !== "TAB") return;
        const elements = this.getFocusableElements(container);
        const first = elements[0];
        const last = elements[elements.length - 1];
        if (event.target == last && !event.shiftKey) {
          event.preventDefault();
          first.focus();
        }
        if ((event.target == container || event.target === first) && event.shiftKey) {
          event.preventDefault();
          last.focus();
        }
      };
      this.trapFocusHandlers.focusin = (event) => {
        const elements = this.getFocusableElements(container);
        const first = elements[0];
        if (!container.contains(event.target)) {
          event.preventDefault();
          first.focus();
          return;
        }
        container.addEventListener("keydown", this.trapFocusHandlers.keydown.bind(this));
      };
      this.trapFocusHandlers.focusout = function() {
        container.removeEventListener("keydown", this.trapFocusHandlers.keydown.bind(this));
      };
      container.addEventListener("focusout", this.trapFocusHandlers.focusout.bind(this));
      container.addEventListener("focusin", this.trapFocusHandlers.focusin.bind(this));
      elementToFocus.focus();
    }
    /**
     * Remove focus trap handlers
     */
    removeTrapFocus(elementToFocus = null) {
      var _a, _b, _c;
      (_a = this.focusElement) == null ? void 0 : _a.removeEventListener("focusin", this.trapFocusHandlers.focusin);
      (_b = this.focusElement) == null ? void 0 : _b.removeEventListener("focusout", this.trapFocusHandlers.focusout);
      (_c = this.focusElement) == null ? void 0 : _c.removeEventListener("keydown", this.trapFocusHandlers.keydown);
      if (elementToFocus) elementToFocus.focus();
    }
    /**
     * Gets and returns all focusable elements in the given container
     */
    getFocusableElements(container) {
      return Array.from(
        container.querySelectorAll(
          "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
        )
      ).filter((element) => {
        const tabindex = element.getAttribute("tabindex");
        return tabindex === null || parseInt(tabindex) >= 0;
      });
    }
    /**
     * Gets the minimum width of the dialog.
     *
     * @returns {number} The minimum width of the dialog.
     */
    get minWidth() {
      return Number(this.getAttribute("dialog-active-min-width"));
    }
    /**
     * Gets the maximum width of the dialog.
     *
     * @returns {number} The maximum width of the dialog.
     */
    get maxWidth() {
      return Number(this.getAttribute("dialog-active-max-width"));
    }
  };
  _handleResize = new WeakMap();
  _previousScrollY = new WeakMap();
  _DialogComponent_instances = new WeakSet();
  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  handleClick_fn = function(event) {
    const { dialog } = this.refs;
    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  };
  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown_fn = function(event) {
    if (event.key !== "Escape") return;
    event.preventDefault();
    this.closeDialog();
  };
  if (!customElements.get("dialog-component")) customElements.define("dialog-component", DialogComponent);
  var _DialogOpenEvent = class _DialogOpenEvent extends CustomEvent {
    constructor() {
      super(_DialogOpenEvent.eventName);
    }
  };
  __publicField(_DialogOpenEvent, "eventName", "dialog:open");
  var DialogOpenEvent = _DialogOpenEvent;
  var _DialogCloseEvent = class _DialogCloseEvent extends CustomEvent {
    constructor() {
      super(_DialogCloseEvent.eventName);
    }
  };
  __publicField(_DialogCloseEvent, "eventName", "dialog:close");
  var DialogCloseEvent = _DialogCloseEvent;
  document.addEventListener(
    "toggle",
    (event) => {
      if (event.target instanceof HTMLDetailsElement) {
        if (event.target.hasAttribute("scroll-lock")) {
          const { open } = event.target;
          if (open) {
            document.documentElement.setAttribute("scroll-lock", "");
          } else {
            document.documentElement.removeAttribute("scroll-lock");
          }
        }
      }
    },
    { capture: true }
  );

  // src/scripts/_base/morph.js
  var HYDRATION_KEY_ATTRIBUTE = "data-hydration-key";
  var PRESERVED_ATTRIBUTES = [
    "product-grid-view",
    "data-current-checked",
    "data-previous-checked",
    "cart-summary-sticky"
  ];
  var PRESERVED_ATTRIBUTES_SET = new Set(PRESERVED_ATTRIBUTES);
  var MORPH_OPTIONS = {
    childrenOnly: true,
    hydrationMode: false,
    reject(oldNode, newNode) {
      var _a, _b;
      if (newNode.nodeType === Node.TEXT_NODE && ((_a = newNode.nodeValue) == null ? void 0 : _a.trim()) === "") {
        return true;
      }
      if (newNode instanceof HTMLTemplateElement && newNode.shadowRootMode === "open" && oldNode.parentElement && newNode.parentElement && oldNode.parentElement.tagName === newNode.parentElement.tagName && ((_b = oldNode.parentElement) == null ? void 0 : _b.shadowRoot) != null) {
        return true;
      }
      if (newNode.nodeType === Node.COMMENT_NODE && newNode.nodeValue === "shopify:rendered_by_section_api") {
        return true;
      }
      return false;
    },
    onBeforeUpdate(oldNode, newNode) {
      if (!(oldNode instanceof Element) || !(newNode instanceof Element)) return;
      const oldAttrs = oldNode.attributes;
      for (let i = 0; i < oldAttrs.length; i++) {
        const attr = (
          /** @type {Attr} */
          oldAttrs[i]
        );
        if (PRESERVED_ATTRIBUTES_SET.has(attr.name)) {
          const oldValue = attr.value;
          if (oldValue && oldValue !== newNode.getAttribute(attr.name)) {
            newNode.setAttribute(attr.name, oldValue);
          }
        }
      }
      const tagName = oldNode.tagName;
      if (tagName === "FLOATING-PANEL-COMPONENT" || tagName === "FIELDSET") {
        const isFloating = tagName === "FLOATING-PANEL-COMPONENT";
        const matchesOld = isFloating || oldNode.classList.contains("variant-option");
        if (matchesOld && newNode.tagName === tagName) {
          const matchesNew = isFloating || newNode.classList.contains("variant-option");
          if (matchesNew) {
            const oldStyle = oldNode.getAttribute("style");
            if (oldStyle) newNode.setAttribute("style", oldStyle);
          }
        }
      }
      if (oldNode instanceof HTMLElement && newNode instanceof HTMLElement && oldNode.style.viewTransitionName) {
        newNode.style.viewTransitionName = oldNode.style.viewTransitionName;
      }
    },
    onAfterUpdate(node) {
      if (node instanceof Component) {
        queueMicrotask(() => node.updatedCallback());
      }
    }
  };
  function morph(oldTree, newTree, options = MORPH_OPTIONS) {
    if (!oldTree || !newTree) {
      throw new Error("Both oldTree and newTree must be provided");
    }
    if (typeof newTree === "string") {
      const parsedNewTree = new DOMParser().parseFromString(newTree, "text/html").body.firstChild;
      if (!parsedNewTree) {
        throw new Error("newTree string is not valid HTML");
      }
      newTree = parsedNewTree;
    }
    if (options.hydrationMode && oldTree instanceof Element && newTree instanceof Element) {
      morphHydrationByKey(oldTree, newTree, options);
      return oldTree;
    }
    let result;
    if (options.childrenOnly) {
      updateChildren(newTree, oldTree, options);
      result = oldTree;
    } else if (newTree.nodeType === 11) {
      throw new Error("newTree should have one root node (not a DocumentFragment)");
    } else {
      result = walk(newTree, oldTree, options);
    }
    if (result instanceof Element) {
      recreateAppBlockScripts(result);
    }
    return result;
  }
  function collectHydrationTargets(root) {
    const targets = [];
    if (root.hasAttribute(HYDRATION_KEY_ATTRIBUTE)) targets.push(root);
    targets.push(...root.querySelectorAll(`[${HYDRATION_KEY_ATTRIBUTE}]`));
    return targets;
  }
  function morphHydrationByKey(oldRoot, newRoot, options) {
    var _a;
    const oldTargets = collectHydrationTargets(oldRoot);
    const newTargets = collectHydrationTargets(newRoot);
    const oldTargetsByKey = /* @__PURE__ */ new Map();
    for (const oldTarget of oldTargets) {
      const key = oldTarget.getAttribute(HYDRATION_KEY_ATTRIBUTE);
      if (key == null || key === "") continue;
      const existing = (_a = oldTargetsByKey.get(key)) != null ? _a : [];
      existing.push(oldTarget);
      oldTargetsByKey.set(key, existing);
    }
    for (const newTarget of newTargets) {
      const key = newTarget.getAttribute(HYDRATION_KEY_ATTRIBUTE);
      if (key == null || key === "") continue;
      const matches = oldTargetsByKey.get(key);
      const oldTarget = matches == null ? void 0 : matches.shift();
      if (!oldTarget) continue;
      morph(oldTarget, newTarget, {
        ...options,
        hydrationMode: false,
        childrenOnly: false
      });
    }
  }
  function walk(newNode, oldNode, options) {
    var _a;
    if (!oldNode) return newNode;
    if (!newNode) return oldNode;
    if (newNode === oldNode) return oldNode;
    const newType = newNode.nodeType;
    if (newType !== oldNode.nodeType) return newNode;
    if (newType === 1) {
      const oldEl = (
        /** @type {Element} */
        oldNode
      );
      const newEl = (
        /** @type {Element} */
        newNode
      );
      if (oldEl.tagName === "SHOPIFY-ACCELERATED-CHECKOUT-CART") return oldNode;
      if (newEl.tagName !== oldEl.tagName) return newNode;
      if (oldNode.isEqualNode(newNode)) {
        syncFormControlsInSubtree(newEl, oldEl);
        return oldNode;
      }
      const newKey = getNodeKey(newNode, options);
      const oldKey = getNodeKey(oldNode, options);
      if (newKey && oldKey && newKey !== oldKey) return newNode;
      if (oldEl.hasAttribute("data-skip-node-update") && newEl.hasAttribute("data-skip-node-update")) {
        updateChildren(newNode, oldNode, options);
      } else {
        updateNode(newNode, oldNode, options);
        updateChildren(newNode, oldNode, options);
      }
    } else {
      updateNode(newNode, oldNode, options);
    }
    (_a = options.onAfterUpdate) == null ? void 0 : _a.call(options, newNode);
    return oldNode;
  }
  function updateNode(newNode, oldNode, options) {
    var _a;
    (_a = options.onBeforeUpdate) == null ? void 0 : _a.call(options, oldNode, newNode);
    const newType = newNode.nodeType;
    if (newType === 1) {
      const oldEl = (
        /** @type {Element} */
        oldNode
      );
      const newEl = (
        /** @type {Element} */
        newNode
      );
      if (!attributesEqual(oldEl, newEl)) {
        if (newNode instanceof HTMLDetailsElement && oldNode instanceof HTMLDetailsElement || newNode instanceof HTMLDialogElement && oldNode instanceof HTMLDialogElement) {
          if (!newNode.hasAttribute("declarative-open")) {
            newNode.open = oldNode.open;
          }
        }
        if (oldNode instanceof HTMLElement && newNode instanceof HTMLElement) {
          for (const attr of ["slot", "sizes"]) {
            const oldValue = oldNode.getAttribute(attr);
            const newValue = newNode.getAttribute(attr);
            if (oldValue !== newValue) {
              oldValue == null ? newNode.removeAttribute(attr) : newNode.setAttribute(attr, oldValue);
            }
          }
        }
        copyAttributes(newEl, oldEl);
      }
      syncFormControlState(newNode, oldNode);
    } else if (newType === 3 || newType === 8) {
      if (oldNode.nodeValue !== newNode.nodeValue) {
        oldNode.nodeValue = newNode.nodeValue;
      }
    }
  }
  function syncFormControlState(newNode, oldNode) {
    if (newNode instanceof HTMLInputElement && oldNode instanceof HTMLInputElement) {
      updateInput(newNode, oldNode);
    } else if (newNode instanceof HTMLOptionElement && oldNode instanceof HTMLOptionElement) {
      updateAttribute(newNode, oldNode, "selected");
    } else if (newNode instanceof HTMLTextAreaElement && oldNode instanceof HTMLTextAreaElement) {
      updateTextarea(newNode, oldNode);
    }
  }
  function skipsNodeUpdate(newNode, oldNode) {
    return newNode.hasAttribute("data-skip-node-update") && oldNode.hasAttribute("data-skip-node-update");
  }
  function syncFormControlsInSubtree(newNode, oldNode) {
    if (!skipsNodeUpdate(newNode, oldNode)) {
      syncFormControlState(newNode, oldNode);
    }
    const newControls = newNode.querySelectorAll("input, option, textarea");
    if (newControls.length === 0) return;
    const oldControls = oldNode.querySelectorAll("input, option, textarea");
    for (let i = 0; i < newControls.length; i++) {
      const newControl = (
        /** @type {Element} */
        newControls[i]
      );
      const oldControl = (
        /** @type {Element} */
        oldControls[i]
      );
      if (skipsNodeUpdate(newControl, oldControl)) continue;
      syncFormControlState(newControl, oldControl);
    }
  }
  function attributesEqual(a, b) {
    const aAttrs = a.attributes;
    const bAttrs = b.attributes;
    const len = aAttrs.length;
    if (len !== bAttrs.length) return false;
    for (let i = 0; i < len; i++) {
      const aAttr = (
        /** @type {Attr} */
        aAttrs[i]
      );
      const bAttr = (
        /** @type {Attr} */
        bAttrs[i]
      );
      if (!aAttr.isEqualNode(bAttr)) return false;
    }
    return true;
  }
  function getNodeKey(node, options) {
    var _a, _b;
    return (_b = (_a = options == null ? void 0 : options.getNodeKey) == null ? void 0 : _a.call(options, node)) != null ? _b : node instanceof Element ? node.id : void 0;
  }
  function updateAttribute(newNode, oldNode, name) {
    if (newNode[name] !== oldNode[name]) {
      oldNode[name] = newNode[name];
      if (newNode[name] != null) {
        oldNode.setAttribute(name, "");
      } else {
        oldNode.removeAttribute(name);
      }
    }
  }
  function copyAttributes(newNode, oldNode) {
    const oldAttrs = oldNode.attributes;
    const newAttrs = newNode.attributes;
    for (const attr of Array.from(newAttrs)) {
      const { name: attrName, namespaceURI: attrNamespaceURI, value: attrValue } = attr;
      const localName = attr.localName || attrName;
      if (attrName === "src" || attrName === "href" || attrName === "srcset" || attrName === "poster") {
        if (oldNode.getAttribute(attrName) === attrValue) continue;
      }
      if (attrNamespaceURI) {
        const fromValue = oldNode.getAttributeNS(attrNamespaceURI, localName);
        if (fromValue !== attrValue) {
          oldNode.setAttributeNS(attrNamespaceURI, localName, attrValue);
        }
      } else {
        if (!oldNode.hasAttribute(attrName)) {
          oldNode.setAttribute(attrName, attrValue);
        } else {
          const fromValue = oldNode.getAttribute(attrName);
          if (fromValue !== attrValue) {
            if (attrValue === "null" || attrValue === "undefined") {
              oldNode.removeAttribute(attrName);
            } else {
              oldNode.setAttribute(attrName, attrValue);
            }
          }
        }
      }
    }
    for (const attr of Array.from(oldAttrs)) {
      if (attr.specified === false) continue;
      const { name: attrName, namespaceURI: attrNamespaceURI } = attr;
      const localName = attr.localName || attrName;
      if (attrNamespaceURI) {
        if (!newNode.hasAttributeNS(attrNamespaceURI, localName)) {
          oldNode.removeAttributeNS(attrNamespaceURI, localName);
        }
      } else if (!newNode.hasAttribute(attrName)) {
        oldNode.removeAttribute(attrName);
      }
    }
  }
  function updateInput(newNode, oldNode) {
    const newValue = newNode.value;
    updateAttribute(newNode, oldNode, "checked");
    updateAttribute(newNode, oldNode, "disabled");
    if (newNode.indeterminate !== oldNode.indeterminate) {
      oldNode.indeterminate = newNode.indeterminate;
    }
    if (oldNode.type === "file") return;
    if (newValue !== oldNode.value) {
      oldNode.setAttribute("value", newValue);
      oldNode.value = newValue;
    }
    if (newValue === "null") {
      oldNode.value = "";
      oldNode.removeAttribute("value");
    }
    if (!newNode.hasAttributeNS(null, "value")) {
      oldNode.removeAttribute("value");
    } else if (oldNode.type === "range") {
      oldNode.value = newValue;
    }
  }
  function updateTextarea(newNode, oldNode) {
    const newValue = newNode.value;
    if (newValue !== oldNode.value) {
      oldNode.value = newValue;
    }
    const firstChild = oldNode.firstChild;
    if ((firstChild == null ? void 0 : firstChild.nodeType) === Node.TEXT_NODE) {
      if (newValue === "" && firstChild.nodeValue === oldNode.placeholder) {
        return;
      }
      firstChild.nodeValue = newValue;
    }
  }
  function recreateAppBlockScripts(container) {
    const scripts = container.querySelectorAll(".shopify-app-block script[src]");
    for (const script of scripts) {
      if (!(script instanceof HTMLScriptElement)) continue;
      const parent = script.parentElement;
      if (!parent) continue;
      const newScript = document.createElement("script");
      for (const attr of Array.from(script.attributes)) {
        newScript.setAttribute(attr.name, attr.value);
      }
      if (script.textContent) {
        newScript.textContent = script.textContent;
      }
      script.remove();
      parent.appendChild(newScript);
    }
  }
  function updateChildren(newNode, oldNode, options) {
    if (oldNode instanceof Element && oldNode.hasAttribute("data-skip-subtree-update") && newNode instanceof Element && newNode.hasAttribute("data-skip-subtree-update")) {
      return;
    }
    const oldChildren = oldNode.childNodes;
    const newChildren = newNode.childNodes;
    const reject = options.reject;
    let oldChild, newChild, morphed, oldMatch;
    let offset = 0;
    for (let i = 0; ; i++) {
      oldChild = oldChildren[i];
      newChild = newChildren[i - offset];
      if (!oldChild && !newChild) {
        break;
      }
      if (!newChild) {
        oldChild && oldNode.removeChild(oldChild);
        i--;
        continue;
      }
      if (!oldChild) {
        oldNode.appendChild(newChild);
        offset++;
        continue;
      }
      if (same(newChild, oldChild, options)) {
        morphed = walk(newChild, oldChild, options);
        if (morphed !== oldChild) {
          oldNode.replaceChild(morphed, oldChild);
          offset++;
        }
        continue;
      }
      if (reject !== void 0 && reject(oldChild, newChild)) {
        newNode.removeChild(newChild);
        i--;
        continue;
      }
      oldMatch = null;
      const oldChildrenLen = oldChildren.length;
      for (let j = i; j < oldChildrenLen; j++) {
        const potentialOldNode = oldChildren[j];
        if (potentialOldNode && same(potentialOldNode, newChild, options)) {
          oldMatch = potentialOldNode;
          break;
        }
      }
      if (oldMatch) {
        morphed = walk(newChild, oldMatch, options);
        if (morphed !== oldMatch) offset++;
        oldNode.insertBefore(morphed, oldChild);
      } else if (!getNodeKey(newChild, options) && !getNodeKey(oldChild, options)) {
        morphed = walk(newChild, oldChild, options);
        if (morphed !== oldChild) {
          oldNode.replaceChild(morphed, oldChild);
          offset++;
        }
      } else {
        oldNode.insertBefore(newChild, oldChild);
        offset++;
      }
    }
  }
  function same(a, b, options) {
    const aType = a.nodeType;
    const bType = b.nodeType;
    if (aType !== bType) return false;
    if (aType === Node.ELEMENT_NODE) {
      if (a instanceof Element && b instanceof Element && a.tagName !== b.tagName) return false;
      const aKey = getNodeKey(a, options);
      const bKey = getNodeKey(b, options);
      if (aKey && bKey && aKey !== bKey) return false;
    }
    if (aType === Node.TEXT_NODE && bType === Node.TEXT_NODE) {
      const av = a.nodeValue;
      const bv = b.nodeValue;
      return av === bv || (av == null ? void 0 : av.trim()) === (bv == null ? void 0 : bv.trim());
    }
    if (aType === Node.COMMENT_NODE && bType === Node.COMMENT_NODE) return a.nodeValue === b.nodeValue;
    return true;
  }

  // src/scripts/_base/section-renderer.js
  var _cache, _abortControllersBySectionId, _pendingRendersBySectionId, _pendingPromises, _SectionRenderer_instances, renderSection_fn, abortPendingMorph_fn, cachePageSections_fn;
  var SectionRenderer = class {
    constructor() {
      __privateAdd(this, _SectionRenderer_instances);
      /**
       * The cache of section HTML
       * @type {Map<string, string>}
       */
      __privateAdd(this, _cache, /* @__PURE__ */ new Map());
      /**
       * The abort controllers by section ID
       * @type {Map<string, AbortController>}
       */
      __privateAdd(this, _abortControllersBySectionId, /* @__PURE__ */ new Map());
      /**
       * The pending renders by section ID
       * @type {Map<string, { abortController: AbortController, promise: Promise<string> }>}
       */
      __privateAdd(this, _pendingRendersBySectionId, /* @__PURE__ */ new Map());
      /**
       * The pending promises
       * @type {Map<string, Promise<string>>}
       */
      __privateAdd(this, _pendingPromises, /* @__PURE__ */ new Map());
      window.addEventListener("load", __privateMethod(this, _SectionRenderer_instances, cachePageSections_fn).bind(this));
    }
    /**
     * Renders a section
     * @param {string} sectionId - The section ID
     * @param {Object} [options] - The options
     * @param {boolean} [options.cache] - Whether to use the cache
     * @param {'hydration'|'full'} [options.mode] - Which parts of the section to morph into the DOM
     * @param {boolean} [options.injectStylesheet=false] - When true, extracts
     * `style[data-section-stylesheet]` from the response and injects it into the section wrapper.
     * @param {URL} [options.url] - The URL to render the section from
     * @returns {Promise<string>} The rendered section HTML
     */
    async renderSection(sectionId, options) {
      const { cache = !Shopify.designMode, mode = "full", injectStylesheet = false } = options != null ? options : {};
      const { url } = options != null ? options : {};
      __privateMethod(this, _SectionRenderer_instances, abortPendingMorph_fn).call(this, sectionId);
      const abortController = new AbortController();
      __privateGet(this, _abortControllersBySectionId).set(sectionId, abortController);
      const renderPromise = __privateMethod(this, _SectionRenderer_instances, renderSection_fn).call(this, sectionId, { cache, mode, injectStylesheet, url }, abortController);
      __privateGet(this, _pendingRendersBySectionId).set(sectionId, { abortController, promise: renderPromise });
      return renderPromise;
    }
    /**
     * Gets the HTML for a section
     * @param {string} sectionId - The section ID
     * @param {boolean} useCache - Whether to use the cache
     * @param {URL} url - The URL to render the section for
     * @param {AbortSignal} [signal] - A signal that cancels the section render fetch
     * @returns {Promise<string>} The rendered section HTML
     */
    async getSectionHTML(sectionId, useCache = true, url = new URL(window.location.href), signal) {
      const sectionUrl = buildSectionRenderingURL(sectionId, url);
      const shouldSharePendingPromise = !signal;
      let pendingPromise = __privateGet(this, _pendingPromises).get(sectionUrl);
      if (pendingPromise) return pendingPromise;
      if (useCache) {
        const cachedHTML = __privateGet(this, _cache).get(sectionUrl);
        console.log("cachedHTML", cachedHTML);
        if (cachedHTML) return cachedHTML;
      }
      pendingPromise = fetch(sectionUrl, { signal }).then((response) => {
        return response.text();
      });
      if (shouldSharePendingPromise) {
        pendingPromise = pendingPromise.finally(() => {
          __privateGet(this, _pendingPromises).delete(sectionUrl);
        });
        __privateGet(this, _pendingPromises).set(sectionUrl, pendingPromise);
      }
      const sectionHTML = await pendingPromise;
      __privateGet(this, _cache).set(sectionUrl, sectionHTML);
      return sectionHTML;
    }
  };
  _cache = new WeakMap();
  _abortControllersBySectionId = new WeakMap();
  _pendingRendersBySectionId = new WeakMap();
  _pendingPromises = new WeakMap();
  _SectionRenderer_instances = new WeakSet();
  renderSection_fn = async function(sectionId, { cache, mode, injectStylesheet, url }, abortController) {
    let sectionHTML = "";
    try {
      sectionHTML = await this.getSectionHTML(sectionId, cache, url, abortController.signal);
    } catch (error) {
      if (abortController.signal.aborted) {
        const pendingRender = __privateGet(this, _pendingRendersBySectionId).get(sectionId);
        if (pendingRender && pendingRender.abortController !== abortController) {
          return pendingRender.promise;
        }
        return sectionHTML;
      }
      throw error;
    } finally {
      if (__privateGet(this, _abortControllersBySectionId).get(sectionId) === abortController) {
        __privateGet(this, _abortControllersBySectionId).delete(sectionId);
      }
      const pendingRender = __privateGet(this, _pendingRendersBySectionId).get(sectionId);
      if ((pendingRender == null ? void 0 : pendingRender.abortController) === abortController) {
        __privateGet(this, _pendingRendersBySectionId).delete(sectionId);
      }
    }
    if (!abortController.signal.aborted) {
      morphSection(sectionId, sectionHTML, { mode, injectStylesheet });
    }
    return sectionHTML;
  };
  /**
   * Aborts an existing render for a section
   * @param {string} sectionId - The section ID
   */
  abortPendingMorph_fn = function(sectionId) {
    const existingAbortController = __privateGet(this, _abortControllersBySectionId).get(sectionId);
    if (existingAbortController) {
      existingAbortController.abort();
      __privateGet(this, _abortControllersBySectionId).delete(sectionId);
    }
  };
  /**
   * Caches the page sections
   */
  cachePageSections_fn = function() {
    for (const section of document.querySelectorAll(".shopify-section")) {
      const url = buildSectionRenderingURL(section.id);
      if (__privateGet(this, _cache).get(url)) return;
      if (containsShadowRoot(section)) return;
      __privateGet(this, _cache).set(url, section.outerHTML);
    }
  };
  var SECTION_ID_PREFIX = "shopify-section-";
  function buildSectionRenderingURL(sectionId, url = new URL(window.location.href)) {
    url.searchParams.set("section_id", normalizeSectionId(sectionId));
    url.searchParams.sort();
    return url.toString();
  }
  function buildSectionSelector(sectionId) {
    return `${SECTION_ID_PREFIX}${sectionId}`;
  }
  function normalizeSectionId(sectionId) {
    return sectionId.replace(new RegExp(`^${SECTION_ID_PREFIX}`), "");
  }
  function containsShadowRoot(element) {
    return !!element.shadowRoot || Array.from(element.children).some(containsShadowRoot);
  }
  async function morphSection(sectionId, html, options = {}) {
    const { mode = "full", injectStylesheet = false } = options;
    const fragment = new DOMParser().parseFromString(html, "text/html");
    const existingElement = document.getElementById(buildSectionSelector(sectionId));
    const newElement = fragment.getElementById(buildSectionSelector(sectionId));
    if (!existingElement) {
      throw new Error(`Section ${sectionId} not found`);
    }
    if (!newElement) {
      throw new Error(`Section ${sectionId} not found in the section rendering response`);
    }
    morph(existingElement, newElement, {
      ...MORPH_OPTIONS,
      hydrationMode: mode === "hydration"
    });
    if (injectStylesheet) {
      injectSectionStylesheet(fragment, existingElement);
    }
  }
  function injectSectionStylesheet(fragment, sectionElement) {
    const newStylesheet = fragment.querySelector("style[data-section-stylesheet]");
    if (!newStylesheet) return;
    const existingStylesheet = sectionElement.querySelector("style[data-section-stylesheet]");
    if (existingStylesheet) {
      existingStylesheet.textContent = newStylesheet.textContent;
    } else {
      sectionElement.prepend(newStylesheet);
    }
  }
  var sectionRenderer = new SectionRenderer();

  // src/scripts/_base/recently-viewed-products.js
  var _STORAGE_KEY, _MAX_PRODUCTS;
  var RecentlyViewed = class {
    /**
     * Adds a product to the recently viewed products list.
     * @param {string} productId - The ID of the product to add.
     */
    static addProduct(productId) {
      let viewedProducts = this.getProducts();
      viewedProducts = viewedProducts.filter((id) => id !== productId);
      viewedProducts.unshift(productId);
      viewedProducts = viewedProducts.slice(0, __privateGet(this, _MAX_PRODUCTS));
      localStorage.setItem(__privateGet(this, _STORAGE_KEY), JSON.stringify(viewedProducts));
    }
    static clearProducts() {
      localStorage.removeItem(__privateGet(this, _STORAGE_KEY));
    }
    /**
     * Retrieves the list of recently viewed products from session storage.
     * @returns {string[]} The list of viewed products.
     */
    static getProducts() {
      return JSON.parse(localStorage.getItem(__privateGet(this, _STORAGE_KEY)) || "[]");
    }
  };
  _STORAGE_KEY = new WeakMap();
  _MAX_PRODUCTS = new WeakMap();
  /** @static @constant {string} The key used to store the viewed products in session storage */
  __privateAdd(RecentlyViewed, _STORAGE_KEY, "viewedProducts");
  /** @static @constant {number} The maximum number of products to store */
  __privateAdd(RecentlyViewed, _MAX_PRODUCTS, 4);

  // src/scripts/_base/events.js
  var ThemeEvents = class {
  };
  /** @static @constant {string} Event triggered when a variant is selected */
  __publicField(ThemeEvents, "variantSelected", "variant:selected");
  /** @static @constant {string} Event triggered when a variant is changed */
  __publicField(ThemeEvents, "variantUpdate", "variant:update");
  /** @static @constant {string} Event triggered when the cart items or quantities are updated */
  __publicField(ThemeEvents, "cartUpdate", "cart:update");
  /** @static @constant {string} Event triggered when a cart update fails */
  __publicField(ThemeEvents, "cartError", "cart:error");
  /** @static @constant {string} Event triggered when a media (video, 3d model) is loaded */
  __publicField(ThemeEvents, "mediaStartedPlaying", "media:started-playing");
  // Event triggered when quantity-selector value is changed
  __publicField(ThemeEvents, "quantitySelectorUpdate", "quantity-selector:update");
  /** @static @constant {string} Event triggered when a predictive search is expanded */
  __publicField(ThemeEvents, "megaMenuHover", "megaMenu:hover");
  /** @static @constant {string} Event triggered when a zoom dialog media is selected */
  __publicField(ThemeEvents, "zoomMediaSelected", "zoom-media:selected");
  /** @static @constant {string} Event triggered when a discount is applied */
  __publicField(ThemeEvents, "discountUpdate", "discount:update");
  /** @static @constant {string} Event triggered when changing collection filters */
  __publicField(ThemeEvents, "FilterUpdate", "filter:update");
  var _CartAddEvent = class _CartAddEvent extends Event {
    /**
     * Creates a new CartAddEvent
     * @param {Object} [resource] - The new cart object
     * @param {Object} [data] - Additional event data
     * @param {boolean} [data.didError] - Whether the cart operation failed
     * @param {string} [data.source] - The source of the cart update
     * @param {string} [data.productId] - The id of the product card that was updated
     * @param {string} [data.variantId] - The id of the product variant that was added
     * @param {Record<string, string>} [data.sections] - The sections affected by the cart operation
     */
    constructor(resource, data) {
      super(_CartAddEvent.eventName, { bubbles: true });
      this.detail = {
        resource,
        data: {
          ...data
        }
      };
    }
  };
  __publicField(_CartAddEvent, "eventName", ThemeEvents.cartUpdate);
  var CartAddEvent = _CartAddEvent;
  function getSearchParams(searchParams) {
    var _a, _b;
    return searchParams != null ? searchParams : new URL((_b = (_a = globalThis.location) == null ? void 0 : _a.href) != null ? _b : "").searchParams;
  }
  function decodeFilterSegment(segment) {
    return decodeURIComponent(segment.replace(/\+/g, " "));
  }
  function parseLocalizedNumber(value, locale) {
    var _a, _b, _c, _d, _e, _f;
    try {
      const resolvedLocale = (_b = locale != null ? locale : (_a = globalThis.Shopify) == null ? void 0 : _a.locale) != null ? _b : "en";
      const parts = new Intl.NumberFormat(resolvedLocale).formatToParts(1234.5);
      const group = (_d = (_c = parts.find((part) => part.type === "group")) == null ? void 0 : _c.value) != null ? _d : ",";
      const decimal = (_f = (_e = parts.find((part) => part.type === "decimal")) == null ? void 0 : _e.value) != null ? _f : ".";
      const parsed = Number(value.split(group).join("").replace(decimal, "."));
      if (!Number.isNaN(parsed)) return parsed;
    } catch (error) {
    }
    return Number(value.replace(/[^\d.]/g, "")) || 0;
  }
  function parseProductFilters(searchParams, options) {
    const params = getSearchParams(searchParams);
    const locale = options == null ? void 0 : options.locale;
    const filters = [];
    const price = {};
    const min = params.get("filter.v.price.gte");
    const max = params.get("filter.v.price.lte");
    if (min) price.min = parseLocalizedNumber(min, locale);
    if (max) price.max = parseLocalizedNumber(max, locale);
    if (price.min !== void 0 || price.max !== void 0) filters.push({ price });
    const availability = params.getAll("filter.v.availability");
    const inStock = availability.includes("1");
    const outOfStock = availability.includes("0");
    if (inStock && !outOfStock) {
      filters.push({ available: true });
    } else if (outOfStock && !inStock) {
      filters.push({ available: false });
    }
    for (const productType of params.getAll("filter.p.product_type")) filters.push({ productType });
    for (const productVendor of params.getAll("filter.p.vendor")) filters.push({ productVendor });
    for (const tag of params.getAll("filter.p.tag")) filters.push({ tag });
    for (const [key, value] of params.entries()) {
      const variantOption = key.match(/^filter\.v\.option\.(.+)$/);
      if (variantOption) {
        filters.push({ variantOption: { name: decodeFilterSegment(variantOption[1]), value } });
        continue;
      }
      const productMetafield = key.match(/^filter\.p\.m\.([^.]+)\.(.+)$/);
      if (productMetafield) {
        filters.push({
          productMetafield: {
            namespace: decodeFilterSegment(productMetafield[1]),
            key: decodeFilterSegment(productMetafield[2]),
            value
          }
        });
        continue;
      }
      const variantMetafield = key.match(/^filter\.v\.m\.([^.]+)\.(.+)$/);
      if (variantMetafield) {
        filters.push({
          variantMetafield: {
            namespace: decodeFilterSegment(variantMetafield[1]),
            key: decodeFilterSegment(variantMetafield[2]),
            value
          }
        });
        continue;
      }
      const taxonomyMetafield = key.match(/^filter\.v\.t\.([^.]+)\.(.+)$/);
      if (taxonomyMetafield) {
        filters.push({
          taxonomyMetafield: {
            key: `${decodeFilterSegment(taxonomyMetafield[1])}.${decodeFilterSegment(taxonomyMetafield[2])}`,
            value
          }
        });
      }
    }
    return filters.length > 0 ? filters : void 0;
  }
  function getSearchSortKey(searchParams) {
    const sortBy = getSearchParams(searchParams).get("sort_by");
    if (!sortBy) return void 0;
    return sortBy.startsWith("price-") ? "PRICE" : "RELEVANCE";
  }
  var ShopifyStandardEvent = class extends Event {
    /**
     * @param {string} type - The standard event name
     * @param {Record<string, any>} payload - Event payload, assigned onto the instance
     * @param {EventInit} [options]
     */
    constructor(type, payload, options) {
      super(type, { bubbles: true, cancelable: true, ...options });
      Object.assign(this, payload);
    }
  };
  /** @static @constant {string} */
  __publicField(ShopifyStandardEvent, "eventName", "");
  var _SearchUpdateEvent = class _SearchUpdateEvent extends ShopifyStandardEvent {
    /** @param {SearchUpdatePayload} payload */
    constructor(payload) {
      super(_SearchUpdateEvent.eventName, payload);
    }
  };
  /** @static @constant {string} */
  __publicField(_SearchUpdateEvent, "eventName", "shopify:search:update");
  /**
   * Creates the deferred promise handed to the event payload.
   * @returns {SearchUpdateDeferred}
   */
  __publicField(_SearchUpdateEvent, "createPromise", () => {
    const deferred = (
      /** @type {any} */
      {}
    );
    deferred.promise = new Promise((resolve, reject) => {
      deferred.resolve = resolve;
      deferred.reject = reject;
    });
    return deferred;
  });
  __publicField(_SearchUpdateEvent, "parseProductFilters", parseProductFilters);
  __publicField(_SearchUpdateEvent, "getSortKey", getSearchSortKey);
  var SearchUpdateEvent = _SearchUpdateEvent;

  // src/scripts/_base/analytics.js
  function pushDataLayer(event, payload = {}) {
    if (!event) return;
    const dataLayer = window.dataLayer = window.dataLayer || [];
    const entry = { event };
    for (const [key, value] of Object.entries(payload)) {
      if (value === void 0 || value === null) continue;
      entry[key] = value;
    }
    dataLayer.push(entry);
  }

  // src/scripts/search/components/search-drawer.js
  var SEARCH_TRACK_DELAY = 1500;
  function escapeHtml(value) {
    if (value == null) return "";
    return String(value).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;");
  }
  var _controller, _activeFetch, _emptyStateLoaded, _triggers, _swiperConfig, _sliders, _searchCount, _resultClicked, _lastQuery, _lastTrackedQuery, _searchTrackTimer, _pendingSearchEvent, _sessionId, _lastTrigger, _handleDialogOpen, _handleDialogClose, _handleKeyboardShortcut, _SearchDrawer_instances, loadEmptyState_fn, resultTargets_get, moveResultFocus_fn, _handleSlideFocus, restoreFocus_fn, _debouncedSearch, resetScrollPositions_fn, getSearchResults_fn, getHelpResults_fn, buildHelpArticles_fn, buildHelpArticleElement_fn, formatReviewSummaries_fn, getRecentlyViewedProductsMarkup_fn, hideResetButton_fn, showResetButton_fn, createAbortController_fn, _resetSearch, resetTracking_fn, clearSearchTrackTimer_fn, queueSearchQueryEvent_fn, flushSearchQueryEvent_fn, _handleResultClick;
  var SearchDrawer = class extends DialogComponent {
    constructor(focusElement) {
      super(focusElement);
      __privateAdd(this, _SearchDrawer_instances);
      __publicField(this, "requiredRefs", ["searchInput", "predictiveSearchResults"]);
      __privateAdd(this, _controller, new AbortController());
      /**
       * @type {AbortController | null}
       */
      __privateAdd(this, _activeFetch, null);
      __privateAdd(this, _emptyStateLoaded, false);
      __privateAdd(this, _triggers, document.querySelectorAll('[data-dialog-trigger="search-drawer-component"]'));
      __publicField(this, "focusElement", this.querySelector("dialog"));
      __privateAdd(this, _swiperConfig, {
        speed: 400,
        slidesPerView: "auto",
        touchAngle: 75,
        threshold: 20,
        freeMode: {
          enabled: true,
          sticky: false
        },
        grabCursor: true
      });
      __privateAdd(this, _sliders, []);
      /**
       * Analytics state for the current drawer session. Reset each time the drawer closes so
       * `search_drawer_close` describes one visit rather than the whole page lifetime.
       */
      __privateAdd(this, _searchCount, 0);
      __privateAdd(this, _resultClicked, false);
      /** The query currently on screen, used to attribute result clicks. */
      __privateAdd(this, _lastQuery, "");
      /** The last query actually reported to GTM. */
      __privateAdd(this, _lastTrackedQuery, "");
      /** @type {ReturnType<typeof setTimeout> | null} */
      __privateAdd(this, _searchTrackTimer, null);
      /** @type {Record<string, any> | null} */
      __privateAdd(this, _pendingSearchEvent, null);
      /**
       * Identifies one drawer session so open -> query -> click -> close can be stitched back
       * together in GA4. Also guarantees consecutive events are never byte-identical, which
       * rules out any downstream collapsing of "duplicate" hits.
       */
      __privateAdd(this, _sessionId, "");
      /** @type {HTMLElement | null} The control that opened the drawer, so focus can return to it. */
      __privateAdd(this, _lastTrigger, null);
      __privateAdd(this, _handleDialogOpen, () => {
        var _a, _b, _c;
        __privateMethod(this, _SearchDrawer_instances, loadEmptyState_fn).call(this);
        __privateMethod(this, _SearchDrawer_instances, resetTracking_fn).call(this);
        __privateSet(this, _sessionId, (_c = (_b = (_a = globalThis.crypto) == null ? void 0 : _a.randomUUID) == null ? void 0 : _b.call(_a)) != null ? _c : `s${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
        pushDataLayer("search_drawer_open", { search_session_id: __privateGet(this, _sessionId) });
      });
      __privateAdd(this, _handleDialogClose, () => {
        __privateSet(this, _lastTrigger, null);
        __privateMethod(this, _SearchDrawer_instances, flushSearchQueryEvent_fn).call(this);
        pushDataLayer("search_drawer_close", {
          search_session_id: __privateGet(this, _sessionId) || void 0,
          search_term: __privateGet(this, _lastTrackedQuery),
          search_query_count: __privateGet(this, _searchCount),
          search_result_clicked: __privateGet(this, _resultClicked),
          // Searched but never clicked through — the abandonment case worth segmenting on.
          search_abandoned: __privateGet(this, _searchCount) > 0 && !__privateGet(this, _resultClicked)
        });
        __privateMethod(this, _SearchDrawer_instances, resetTracking_fn).call(this);
        __privateGet(this, _resetSearch).call(this);
      });
      /**
       * Handles the CMD+K key combination.
       * @param {KeyboardEvent} event - The keyboard event.
       */
      __privateAdd(this, _handleKeyboardShortcut, (event) => {
        var _a;
        if (event.metaKey && event.key === "k") {
          (_a = this.dialog) == null ? void 0 : _a.toggleDialog();
        }
      });
      /**
       * Bring a keyboard-focused card into its carousel's viewport.
       *
       * @param {FocusEvent} event - The focusin event from a slider element.
       */
      __privateAdd(this, _handleSlideFocus, (event) => {
        const sliderElement = event.currentTarget;
        const target = event.target;
        if (!(sliderElement instanceof HTMLElement) || !(target instanceof Element)) return;
        const slide = target.closest(".swiper-slide");
        const swiper = __privateGet(this, _sliders).find((instance) => (instance == null ? void 0 : instance.el) === sliderElement);
        if (!slide || !swiper || swiper.destroyed || !swiper.slides) return;
        const index = Array.from(swiper.slides).indexOf(slide);
        if (index === -1) return;
        const slideRect = slide.getBoundingClientRect();
        const viewportRect = sliderElement.getBoundingClientRect();
        const isFullyVisible = slideRect.left >= viewportRect.left - 1 && slideRect.right <= viewportRect.right + 1;
        if (!isFullyVisible) {
          swiper.slideTo(index, prefersReducedMotion() ? 0 : void 0);
        }
        requestAnimationFrame(() => {
          sliderElement.scrollLeft = 0;
          sliderElement.scrollTop = 0;
        });
      });
      __publicField(this, "handleQueryClick", (event) => {
        const target = event.target;
        const searchInput = this.refs.searchInput;
        if (!target) return;
        const newQuery = target.textContent.replace(/\s+/g, " ").trim();
        pushDataLayer("popular_search_click", {
          search_session_id: __privateGet(this, _sessionId) || void 0,
          search_term: newQuery,
          search_previous_term: __privateGet(this, _lastQuery) || void 0,
          // Empty-state chips vs. the query suggestions Shopify returns alongside results.
          search_suggestion_source: target.closest(".predictive-search-results__suggestion") ? "popular_search" : "suggested_query"
        });
        searchInput.value = newQuery;
        searchInput.focus();
        __privateMethod(this, _SearchDrawer_instances, getSearchResults_fn).call(this, newQuery);
      });
      /**
       * Arrow keys walk the results, Escape clears the query.
       *
       * Tab is deliberately left to the browser. The drawer's controls already sit in reading
       * order in the DOM, so the native tab order is the semantically correct one; this handler
       * used to intercept Tab and push focus straight back into the input, which is why focus
       * appeared to be stuck there.
       *
       * Delegated from the form, so it also sees keypresses from a focused result.
       *
       * @param {KeyboardEvent} event - The keyboard event.
       */
      __publicField(this, "onSearchKeyDown", (event) => {
        if (event.key === "Escape") {
          __privateGet(this, _resetSearch).call(this);
          return;
        }
        switch (event.key) {
          case "ArrowDown":
            if (__privateMethod(this, _SearchDrawer_instances, moveResultFocus_fn).call(this, 1)) event.preventDefault();
            break;
          case "ArrowUp":
            if (__privateMethod(this, _SearchDrawer_instances, moveResultFocus_fn).call(this, -1)) event.preventDefault();
            break;
          case "Enter":
            if (event.target === this.refs.searchInput) event.preventDefault();
            break;
        }
      });
      /**
       * Reset the search state.
       * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
       */
      __publicField(this, "resetSearch", debounce((keepFocus = true) => {
        if (keepFocus) {
          this.refs.searchInput.focus();
        }
        __privateGet(this, _resetSearch).call(this);
      }, 100));
      /**
       * Input handler. Deliberately NOT debounced: it has to see every keystroke so it can hold
       * back the analytics report while the shopper is still composing. The fetch stays
       * debounced in `#debouncedSearch`.
       * @param {InputEvent} event
       */
      __publicField(this, "search", (event) => {
        if (!event.inputType) return;
        __privateMethod(this, _SearchDrawer_instances, clearSearchTrackTimer_fn).call(this);
        __privateGet(this, _debouncedSearch).call(this, event);
      });
      /**
       * Debounce the search handler to fetch and display search results based on the input value.
       * Reset the current selection index and close results if the search term is empty.
       */
      __privateAdd(this, _debouncedSearch, debounce((event) => {
        if (!event.inputType) return;
        const searchTerm = this.refs.searchInput.value.trim();
        if (!searchTerm.length) {
          __privateGet(this, _resetSearch).call(this);
          return;
        }
        __privateMethod(this, _SearchDrawer_instances, showResetButton_fn).call(this);
        __privateMethod(this, _SearchDrawer_instances, getSearchResults_fn).call(this, searchTerm);
      }, 200));
      __privateAdd(this, _resetSearch, async () => {
        const { predictiveSearchResults, searchInput } = this.refs;
        const emptySectionId = "predictive-search-empty";
        searchInput.value = "";
        __privateSet(this, _lastQuery, "");
        __privateMethod(this, _SearchDrawer_instances, clearSearchTrackTimer_fn).call(this);
        __privateSet(this, _pendingSearchEvent, null);
        __privateMethod(this, _SearchDrawer_instances, hideResetButton_fn).call(this);
        const abortController = __privateMethod(this, _SearchDrawer_instances, createAbortController_fn).call(this);
        const url = new URL(window.location.href);
        url.searchParams.delete("page");
        const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
        const parsedEmptySectionMarkup = new DOMParser().parseFromString(emptySectionMarkup, "text/html").querySelector(".predictive-search-empty-section");
        if (!parsedEmptySectionMarkup) throw new Error("No empty section markup found");
        const viewedProducts = RecentlyViewed.getProducts();
        if (viewedProducts.length > 2) {
          const recentlyViewedMarkup = await __privateMethod(this, _SearchDrawer_instances, getRecentlyViewedProductsMarkup_fn).call(this);
          if (!recentlyViewedMarkup) return;
          const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, "text/html");
          const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById("predictive-search-products");
          if (!recentlyViewedProductsHtml) return;
          for (const child of recentlyViewedProductsHtml.children) {
            if (child instanceof HTMLElement) {
              child.setAttribute("ref", "recentlyViewedWrapper");
            }
          }
          const collectionElement = parsedEmptySectionMarkup.querySelector("#predictive-search-products");
          if (!collectionElement) return;
          collectionElement.replaceChildren(...recentlyViewedProductsHtml.children);
        }
        if (abortController.signal.aborted) return;
        const result = morph(predictiveSearchResults, parsedEmptySectionMarkup);
        __privateMethod(this, _SearchDrawer_instances, restoreFocus_fn).call(this);
        this.refreshSliders(predictiveSearchResults);
        __privateMethod(this, _SearchDrawer_instances, resetScrollPositions_fn).call(this);
      });
      /**
       * Report a click on a result card. Attributes carry the item identity — see
       * `snippets/search-resource-item.liquid`.
       * @param {MouseEvent} event
       */
      __privateAdd(this, _handleResultClick, (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;
        if (!target.closest("a[href]")) return;
        const item = target.closest("[data-search-item]");
        if (!(item instanceof HTMLElement)) return;
        __privateMethod(this, _SearchDrawer_instances, flushSearchQueryEvent_fn).call(this);
        __privateSet(this, _resultClicked, true);
        const { searchItemType, searchItemId, searchItemTitle, searchItemHandle, searchItemUrl, searchItemPosition } = item.dataset;
        pushDataLayer("search_result_click", {
          search_session_id: __privateGet(this, _sessionId) || void 0,
          search_term: __privateGet(this, _lastQuery),
          search_result_type: searchItemType,
          search_result_id: searchItemId,
          search_result_title: searchItemTitle,
          search_result_handle: searchItemHandle,
          search_result_url: searchItemUrl,
          search_result_position: searchItemPosition ? Number(searchItemPosition) : void 0
        });
      });
    }
    /**
     * Get the dialog component.
     * @returns {DialogComponent | null} The dialog component.
     */
    get dialog() {
      return this.closest("dialog-component");
    }
    connectedCallback() {
      var _a;
      super.connectedCallback();
      const { signal } = __privateGet(this, _controller);
      document.addEventListener("keydown", __privateGet(this, _handleKeyboardShortcut), {
        signal
      });
      this.addEventListener(DialogCloseEvent.eventName, __privateGet(this, _handleDialogClose), {
        signal
      });
      this.addEventListener(DialogOpenEvent.eventName, __privateGet(this, _handleDialogOpen), {
        signal
      });
      (_a = this.refs.predictiveSearchResults) == null ? void 0 : _a.addEventListener("click", __privateGet(this, _handleResultClick), { signal });
      const warmEmptyState = () => __privateMethod(this, _SearchDrawer_instances, loadEmptyState_fn).call(this);
      __privateGet(this, _triggers).forEach((trigger) => {
        trigger.addEventListener("click", this.open.bind(this), { signal });
        trigger.addEventListener("pointerenter", warmEmptyState, {
          signal,
          once: true
        });
        trigger.addEventListener("focusin", warmEmptyState, {
          signal,
          once: true
        });
        trigger.addEventListener("touchstart", warmEmptyState, {
          signal,
          once: true,
          passive: true
        });
      });
      if (this.refs.searchInput.value.length > 0) {
        __privateMethod(this, _SearchDrawer_instances, showResetButton_fn).call(this);
      }
      this.refreshSliders(this.refs.predictiveSearchResults);
    }
    /**
     * @param {Event} [event] - The click that opened the drawer.
     */
    open(event) {
      const trigger = event == null ? void 0 : event.currentTarget;
      __privateSet(this, _lastTrigger, trigger instanceof HTMLElement ? trigger : null);
      __privateMethod(this, _SearchDrawer_instances, loadEmptyState_fn).call(this);
      this.showDialog();
    }
    close() {
      this.closeDialog();
    }
    disconnectedCallback() {
      super.disconnectedCallback();
      __privateGet(this, _controller).abort();
      __privateMethod(this, _SearchDrawer_instances, clearSearchTrackTimer_fn).call(this);
    }
    /**
     * Clears the recently viewed products.
     * @param {Event} event - The event.
     */
    clearRecentlyViewedProducts(event) {
      event.stopPropagation();
      RecentlyViewed.clearProducts();
      const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;
      const allRecentlyViewedElements = [...recentlyViewedItems || [], ...recentlyViewedTitle || []];
      if (allRecentlyViewedElements.length === 0) {
        return;
      }
      if (recentlyViewedWrapper) {
        recentlyViewedWrapper.classList.add("removing");
        onAnimationEnd(recentlyViewedWrapper, () => {
          recentlyViewedWrapper.remove();
        });
      }
    }
    /**
     * Destroy any existing sliders and initialize one per slider element inside the container.
     * The container must already be in the document — Swiper resolves string selectors against
     * `document`, so it is passed the element itself rather than a selector.
     * @param {Element} predictiveSearchResults - The live results container.
     */
    refreshSliders(predictiveSearchResults) {
      if (!predictiveSearchResults) return;
      const swiperAvailable = typeof Swiper === "function";
      __privateGet(this, _sliders).forEach((slider) => {
        slider.destroy();
      });
      __privateSet(this, _sliders, []);
      const sliders = predictiveSearchResults.querySelectorAll(".predictive-search-results__slider");
      sliders.forEach((slider) => {
        const id = slider.dataset.sliderId;
        slider.addEventListener("focusin", __privateGet(this, _handleSlideFocus), { signal: __privateGet(this, _controller).signal });
        if (swiperAvailable) {
          try {
            __privateGet(this, _sliders).push(
              new Swiper(slider, {
                ...__privateGet(this, _swiperConfig),
                navigation: {
                  nextEl: `.predictive-search-results__next[data-slider-id='${id}']`,
                  prevEl: `.predictive-search-results__prev[data-slider-id='${id}']`
                },
                scrollbar: {
                  el: `.predictive-search-results__scrollbar[data-slider-id='${id}']`,
                  draggable: true
                }
              })
            );
          } catch (error) {
            console.warn(`[search-drawer] Could not initialise the "${id}" slider`, error);
          }
        }
        __privateMethod(this, _SearchDrawer_instances, formatReviewSummaries_fn).call(this, slider);
      });
    }
  };
  _controller = new WeakMap();
  _activeFetch = new WeakMap();
  _emptyStateLoaded = new WeakMap();
  _triggers = new WeakMap();
  _swiperConfig = new WeakMap();
  _sliders = new WeakMap();
  _searchCount = new WeakMap();
  _resultClicked = new WeakMap();
  _lastQuery = new WeakMap();
  _lastTrackedQuery = new WeakMap();
  _searchTrackTimer = new WeakMap();
  _pendingSearchEvent = new WeakMap();
  _sessionId = new WeakMap();
  _lastTrigger = new WeakMap();
  _handleDialogOpen = new WeakMap();
  _handleDialogClose = new WeakMap();
  _handleKeyboardShortcut = new WeakMap();
  _SearchDrawer_instances = new WeakSet();
  loadEmptyState_fn = function() {
    if (__privateGet(this, _emptyStateLoaded)) return;
    __privateSet(this, _emptyStateLoaded, true);
    this.resetSearch(false);
  };
  resultTargets_get = function() {
    const { predictiveSearchResults } = this.refs;
    if (!predictiveSearchResults) return [];
    return this.getFocusableElements(predictiveSearchResults).filter(
      // `inert` and `display: none` subtrees survive the selector but are not reachable:
      // product card titles are inert, and the carousel arrows are hidden by CSS.
      (element) => !element.closest("[inert]") && element.getClientRects().length > 0
    );
  };
  /**
   * Walk focus through the results with the arrow keys.
   *
   * @param {1 | -1} direction - Forwards or backwards through the results.
   * @returns {boolean} Whether focus moved, so the caller knows to swallow the keypress.
   */
  moveResultFocus_fn = function(direction) {
    var _a, _b;
    const targets = __privateGet(this, _SearchDrawer_instances, resultTargets_get);
    if (!targets.length) return false;
    const active = document.activeElement;
    const currentIndex = active instanceof HTMLElement ? targets.indexOf(active) : -1;
    if (currentIndex === -1) {
      (_a = targets[direction === 1 ? 0 : targets.length - 1]) == null ? void 0 : _a.focus();
      return true;
    }
    const nextIndex = currentIndex + direction;
    if (nextIndex < 0) {
      this.refs.searchInput.focus();
      return true;
    }
    if (nextIndex >= targets.length) return false;
    (_b = targets[nextIndex]) == null ? void 0 : _b.focus();
    return true;
  };
  _handleSlideFocus = new WeakMap();
  /**
   * Morphing in new results can replace whichever card had focus, which drops focus to
   * `<body>` and restarts tabbing at the top of the page. Put it back on the input.
   */
  restoreFocus_fn = function() {
    var _a, _b;
    if (!((_a = this.refs.dialog) == null ? void 0 : _a.open)) return;
    const active = document.activeElement;
    if (active instanceof HTMLElement && active !== document.body && this.contains(active)) return;
    (_b = this.refs.searchInput) == null ? void 0 : _b.focus();
  };
  _debouncedSearch = new WeakMap();
  /**
   * Resets scroll positions for search results containers
   */
  resetScrollPositions_fn = function() {
    requestAnimationFrame(() => {
      var _a, _b, _c;
      (_b = (_a = this.refs.predictiveSearchResults) == null ? void 0 : _a.querySelector(".predictive-search-results__inner")) == null ? void 0 : _b.scrollTo(0, 0);
      (_c = this.querySelector(".predictive-search-form__content")) == null ? void 0 : _c.scrollTo(0, 0);
    });
  };
  getSearchResults_fn = async function(searchTerm) {
    if (!this.dataset.sectionId) return;
    __privateSet(this, _lastQuery, searchTerm);
    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set("q", searchTerm);
    url.searchParams.set("resources[limit]", "10");
    url.searchParams.set("resources[limit_scope]", "each");
    url.searchParams.set("resources[type]", "product,query,article");
    url.searchParams.set("resources[options][fields]", "title,product_type,body,tag,variants.sku,author");
    const { predictiveSearchResults } = this.refs;
    const abortController = __privateMethod(this, _SearchDrawer_instances, createAbortController_fn).call(this);
    const deferredPromise = SearchUpdateEvent.createPromise();
    this.dispatchEvent(
      new SearchUpdateEvent({
        search: {
          query: searchTerm
        },
        promise: deferredPromise.promise
      })
    );
    const helpArticles = await __privateMethod(this, _SearchDrawer_instances, getHelpResults_fn).call(this, searchTerm, abortController.signal);
    sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url).then((resultsMarkup) => {
      if (!resultsMarkup) {
        deferredPromise.resolve({ totalCount: 0 });
        return;
      }
      if (abortController.signal.aborted) {
        deferredPromise.reject(new Error("Fetch aborted by user"));
        return;
      }
      morph(predictiveSearchResults, resultsMarkup);
      __privateMethod(this, _SearchDrawer_instances, restoreFocus_fn).call(this);
      __privateMethod(this, _SearchDrawer_instances, resetScrollPositions_fn).call(this, predictiveSearchResults);
      const resultCount = predictiveSearchResults.querySelectorAll('[ref="resultsItems[]"]').length + helpArticles.length;
      deferredPromise.resolve({ totalCount: resultCount });
      __privateMethod(this, _SearchDrawer_instances, queueSearchQueryEvent_fn).call(this, {
        search_term: searchTerm,
        search_results_count: resultCount
      });
      this.refreshSliders(predictiveSearchResults);
      __privateMethod(this, _SearchDrawer_instances, buildHelpArticles_fn).call(this, predictiveSearchResults, helpArticles);
    }).catch((error) => {
      deferredPromise.reject(error);
      if (abortController.signal.aborted) return;
      throw error;
    });
  };
  getHelpResults_fn = async function(searchTerm, signal) {
    var _a;
    const ALGOLIA = {
      app: "LO28MF0H5E",
      index: "entities-v2",
      key: "NzgyMGQ1MzZkMTk1M2QzMWM2MTc4NmJjNTM1ZWNlZDM0Zjk2ZTA3NmZlMGY5ZTFjOTBjZTljNWJlODEyNDA4MWZpbHRlcnM9aGVscF9jZW50ZXJfaWQlM0ExODg3JTIwQU5EJTIwY3VzdG9tZXJfdmlzaWJpbGl0eSUzQVBVQkxJQyUyMCUyMCUyMCUyMCUyMCUyMCUyMCUyMCUyMEFORCUyMGN1cnJlbnQlMjAlMjAlMjAlMjAlMjAlMjAlMjAlMjAlMjBBTkQlMjAoTk9UJTIwcGFyZW50X2NhdGVnb3J5XzEuY3VzdG9tZXJfdmlzaWJpbGl0eSUzQVVOTElTVEVEKSUyMCUyMCUyMCUyMCUyMCUyMCUyMCUyMCUyMEFORCUyMChOT1QlMjBwYXJlbnRfY2F0ZWdvcnlfMi5jdXN0b21lcl92aXNpYmlsaXR5JTNBVU5MSVNURUQpJTIwJTIwJTIwJTIwJTIwJTIwJTIwJTIwJTIwQU5EJTIwKE5PVCUyMHBhcmVudF9jYXRlZ29yeV8zLmN1c3RvbWVyX3Zpc2liaWxpdHklM0FVTkxJU1RFRCklMjAlMjAlMjAlMjAlMjAlMjAlMjAlMjAlMjBBTkQlMjAoTk9UJTIwcGFyZW50X2NhdGVnb3J5XzQuY3VzdG9tZXJfdmlzaWJpbGl0eSUzQVVOTElTVEVEKSUyMCUyMCUyMCUyMCUyMCUyMCUyMCUyMCUyMEFORCUyMChOT1QlMjBwYXJlbnRfY2F0ZWdvcnlfNS5jdXN0b21lcl92aXNpYmlsaXR5JTNBVU5MSVNURUQpJnJlc3RyaWN0SW5kaWNlcz1lbnRpdGllcy12Mg=="
    };
    try {
      const res = await fetch(`https://${ALGOLIA.app}-dsn.algolia.net/1/indexes/${ALGOLIA.index}/query`, {
        method: "POST",
        signal,
        headers: {
          "X-Algolia-API-Key": ALGOLIA.key,
          "X-Algolia-Application-Id": ALGOLIA.app,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ query: searchTerm, hitsPerPage: 5, filters: "type:article" })
      });
      if (!res.ok) return [];
      const { hits } = (_a = await res.json()) != null ? _a : {};
      if (!Array.isArray(hits)) return [];
      return hits.slice(0, 3).map((hit) => {
        var _a2;
        return {
          title: hit == null ? void 0 : hit.title,
          preview: hit == null ? void 0 : hit.preview,
          category: (_a2 = hit == null ? void 0 : hit.parent_category_1) == null ? void 0 : _a2.title,
          url: (hit == null ? void 0 : hit.slug) && (hit == null ? void 0 : hit.id) ? `/pages/help?hcUrl=/${hit.slug}-${hit.id}` : null
        };
      }).filter((article) => article.title && article.url);
    } catch (e) {
      return [];
    }
  };
  /**
   * Render the help-center hits into the results.
   *
   * @param {Element | null | undefined} predictiveSearchResults - The live results container.
   * @param {Array<Record<string, any>> | undefined} helpArticles - Hits from `#getHelpResults`.
   */
  buildHelpArticles_fn = function(predictiveSearchResults, helpArticles) {
    const articles = Array.isArray(helpArticles) ? helpArticles : [];
    const helpContainer = predictiveSearchResults == null ? void 0 : predictiveSearchResults.querySelector("#predictive-search-faqs");
    if (!helpContainer) return;
    if (!articles.length) {
      helpContainer.style.display = "none";
      return;
    }
    helpContainer.style.display = "";
    const helpArticleResultsContainer = helpContainer.querySelector(".predictive-search-results__list");
    if (!helpArticleResultsContainer) return;
    const noResultsContainer = predictiveSearchResults.querySelector(".predictive-search-results__no-results");
    if (noResultsContainer && helpArticles.length) {
      noResultsContainer.style.display = "none";
    }
    helpArticleResultsContainer.querySelectorAll(".help-article-card").forEach((card) => card.remove());
    articles.forEach((article) => {
      const articleElement = __privateMethod(this, _SearchDrawer_instances, buildHelpArticleElement_fn).call(this, article);
      if (articleElement) helpArticleResultsContainer.insertAdjacentHTML("beforeend", articleElement);
    });
  };
  /**
   * Build the markup for one help-center card.
   *
   * @param {Record<string, any>} article - A validated hit from `#getHelpResults`.
   * @returns {string} The card markup, or an empty string when the hit is unusable.
   */
  buildHelpArticleElement_fn = function(article) {
    const title = escapeHtml(article == null ? void 0 : article.title);
    const url = escapeHtml(article == null ? void 0 : article.url);
    if (!title || !url) return "";
    const category = escapeHtml(article == null ? void 0 : article.category);
    return `
      <div class='help-article-card' ref='resultsItems[]'>
        <a href='${url}' class='help-article-card__link'>
          <div class='help-article-card__wrapper'>
            <div class='help-article-card__icon-wrap help-article-card__icon-question'>
              <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 13 13" fill="none">
                <g clip-path="url(#clip0_59_11316)">
                  <path d="M6.50016 11.9168C9.49171 11.9168 11.9168 9.49171 11.9168 6.50016C11.9168 3.50862 9.49171 1.0835 6.50016 1.0835C3.50862 1.0835 1.0835 3.50862 1.0835 6.50016C1.0835 9.49171 3.50862 11.9168 6.50016 11.9168Z" stroke="currentColor" stroke-width="1.08333" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M4.9292 4.87495C5.06394 4.52375 5.31635 4.23018 5.64337 4.0443C5.9704 3.85842 6.35178 3.79175 6.72247 3.85566C7.09316 3.91957 7.43019 4.11011 7.67609 4.39477C7.92198 4.67942 8.06151 5.04057 8.07087 5.41662C8.07087 6.49995 6.44587 7.04162 6.44587 7.04162" stroke="currentColor" stroke-width="1.08333" stroke-linecap="round" stroke-linejoin="round"/>
                  <circle cx="6.50016" cy="9.20817" r="0.541667" fill="currentColor"/>
                </g>
              </svg>
            </div>
            <div class='help-article-card__content'>
              <div class='help-article-card__title'>
              ${title}
              </div>
              ${category ? `<div class='help-article-card__category'>${category}</div>` : ""}
            </div>
      
            <div class='help-article-card__icon-wrap help-article-card__icon-arrow'>
              <svg xmlns='http://www.w3.org/2000/svg' width='11' height='14' viewBox='0 0 9 10' fill='none'>
                <path d="M3.80834 9.67995L0.0003438 5.85595V3.83995H0.0803438L3.26434 7.02395V-5.43594e-05H4.80034V7.02395L7.98434 3.83995H8.06434V5.85595L4.25634 9.67995H3.80834Z" fill="currentColor"></path>
              </svg>
            </div>
          </div>
        </a>
      </div>
    `;
  };
  /**
   * Normalise the Okendo rating summaries inside one slider.
   *
   * Okendo hydrates these asynchronously while the drawer re-renders on every keystroke, so
   * this runs against markup that may be missing, half-populated, or already formatted by an
   * earlier pass over a node the morph preserved. Every step has to tolerate all three.
   *
   * @param {Element} slider - A single results carousel.
   */
  formatReviewSummaries_fn = function(slider) {
    const reviews = slider.querySelectorAll("[data-oke-reviews-product-listing-rating]");
    reviews.forEach((reviewEl) => {
      var _a, _b;
      if (!(reviewEl instanceof HTMLElement)) return;
      const reviewsCount = reviewEl.querySelector('.okeReviews-reviewsSummary-ratingCount span[aria-hidden="true"]');
      if (reviewsCount) {
        const digits = ((_a = reviewsCount.textContent) != null ? _a : "").split(" ")[0].replace(/\D/g, "");
        const count = digits ? Number.parseInt(digits, 10) : Number.NaN;
        if (Number.isFinite(count)) {
          reviewsCount.textContent = count.toLocaleString("en-US");
        }
      }
      const reviewsValueEl = reviewEl.querySelector(".okeReviews-reviewsSummary-ratingValue");
      if (!reviewsValueEl) return;
      const reviewValue = ((_b = reviewsValueEl.textContent) != null ? _b : "").split("/")[0].trim();
      const rating = Number.parseFloat(reviewValue);
      if (!reviewValue || !Number.isFinite(rating)) return;
      if (rating === 0) {
        reviewEl.style.display = "none";
      } else {
        reviewEl.style.display = "";
        reviewsValueEl.textContent = reviewValue;
      }
    });
  };
  getRecentlyViewedProductsMarkup_fn = async function() {
    if (!this.dataset.sectionId) return null;
    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length < 3) return null;
    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set("q", viewedProducts.map(
      /** @param {string} id */
      (id) => `id:${id}`
    ).join(" OR "));
    url.searchParams.set("resources[type]", "product");
    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  };
  hideResetButton_fn = function() {
    const { resetButton } = this.refs;
    if (!(resetButton instanceof HTMLElement)) return;
    resetButton.hidden = true;
  };
  showResetButton_fn = function() {
    const { resetButton } = this.refs;
    if (!(resetButton instanceof HTMLElement)) return;
    resetButton.hidden = false;
  };
  createAbortController_fn = function() {
    const abortController = new AbortController();
    if (__privateGet(this, _activeFetch)) {
      __privateGet(this, _activeFetch).abort();
    }
    __privateSet(this, _activeFetch, abortController);
    return abortController;
  };
  _resetSearch = new WeakMap();
  resetTracking_fn = function() {
    __privateMethod(this, _SearchDrawer_instances, clearSearchTrackTimer_fn).call(this);
    __privateSet(this, _pendingSearchEvent, null);
    __privateSet(this, _searchCount, 0);
    __privateSet(this, _resultClicked, false);
    __privateSet(this, _lastQuery, "");
    __privateSet(this, _lastTrackedQuery, "");
    __privateSet(this, _sessionId, "");
  };
  clearSearchTrackTimer_fn = function() {
    if (__privateGet(this, _searchTrackTimer) === null) return;
    clearTimeout(__privateGet(this, _searchTrackTimer));
    __privateSet(this, _searchTrackTimer, null);
  };
  /**
   * Hold a rendered result set back until typing has settled, so one phrase reports once.
   * @param {Record<string, any>} payload
   */
  queueSearchQueryEvent_fn = function(payload) {
    __privateSet(this, _pendingSearchEvent, payload);
    __privateMethod(this, _SearchDrawer_instances, clearSearchTrackTimer_fn).call(this);
    __privateSet(this, _searchTrackTimer, setTimeout(() => __privateMethod(this, _SearchDrawer_instances, flushSearchQueryEvent_fn).call(this), SEARCH_TRACK_DELAY));
  };
  /** Report the queued query, if any. `search_query` and `search_no_results` are exclusive. */
  flushSearchQueryEvent_fn = function() {
    __privateMethod(this, _SearchDrawer_instances, clearSearchTrackTimer_fn).call(this);
    const payload = __privateGet(this, _pendingSearchEvent);
    if (!payload) return;
    __privateSet(this, _pendingSearchEvent, null);
    if (payload.search_term === __privateGet(this, _lastTrackedQuery)) return;
    __privateSet(this, _searchCount, __privateGet(this, _searchCount) + 1);
    __privateSet(this, _lastTrackedQuery, payload.search_term);
    pushDataLayer(payload.search_results_count === 0 ? "search_no_results" : "search_query", {
      ...payload,
      search_session_id: __privateGet(this, _sessionId) || void 0,
      // 1 for the first reported query of this drawer session, 2 for the next, and so on.
      search_sequence: __privateGet(this, _searchCount)
    });
  };
  _handleResultClick = new WeakMap();
  function defineSearchDrawer() {
    if (!customElements.get("search-drawer-component")) {
      customElements.define("search-drawer-component", SearchDrawer);
    }
  }

  // src/scripts/search/search-default.js
  defineSearchDrawer();
})();
